import { useCallback, useEffect, useRef, useState } from "react";
import "./Dashboard.css";
import Button from "./Button";
import EpubUploadModal from "./EpubUploadModal";
import UploadCard from "./UploadCard";
import RecentsRow from "./RecentsRow";

// Module-level cache — survives re-navigation within the same session
let _epubsCache = null;

function Dashboard() {
  const PAGE_SIZE = 12;
  const [user, setUser] = useState(() =>
    JSON.parse(localStorage.getItem("user") || "{}")
  );
  const [profileImageFailed, setProfileImageFailed] = useState(false);
  const [showUploadModal, setShowUploadModal] = useState(false);
  const [epubs, setEpubs] = useState(_epubsCache || []);
  const [epubsLoading, setEpubsLoading] = useState(_epubsCache === null);
  const [epubsOffset, setEpubsOffset] = useState(0);
  const [hasMoreEpubs, setHasMoreEpubs] = useState(true);
  const [epubsLoadingMore, setEpubsLoadingMore] = useState(false);
  const [searchQuery, setSearchQuery] = useState("");
  const [isSearchActive, setIsSearchActive] = useState(false);
  const [searchResults, setSearchResults] = useState([]);
  const [searchLoading, setSearchLoading] = useState(false);
  const [searchOffset, setSearchOffset] = useState(0);
  const [hasMoreSearchResults, setHasMoreSearchResults] = useState(false);
  const [searchLoadingMore, setSearchLoadingMore] = useState(false);
  const searchSectionRef = useRef(null);
  const loadMoreSentinelRef = useRef(null);

  useEffect(() => {
    document.body.style.backgroundColor = "#FFFFFF";
    return () => {
      document.body.style.backgroundColor = "";
    };
  }, []);

  useEffect(() => {
    const handleProfileUpdated = (event) => {
      if (event.detail) {
        setUser(event.detail);
        setProfileImageFailed(false);
      }
    };

    window.addEventListener("profile-updated", handleProfileUpdated);
    return () => window.removeEventListener("profile-updated", handleProfileUpdated);
  }, []);

  const fetchEpubPage = useCallback(async ({ offset = 0, append = false, query = "" } = {}) => {
    try {
      const apiUrl = process.env.REACT_APP_API_URL;
      const params = new URLSearchParams({
        limit: String(PAGE_SIZE),
        offset: String(offset)
      });

      if (query.trim()) {
        params.set("q", query.trim());
      }

      const response = await fetch(`${apiUrl}/get-epubs.php?${params.toString()}`, {
        credentials: "include"
      });
      const data = await response.json();
      if (!data.success) return;

      const nextItems = Array.isArray(data.epubs) ? data.epubs : [];
      const hasMore = Boolean(data.has_more);

      if (query.trim()) {
        setSearchResults((prev) => (append ? [...prev, ...nextItems] : nextItems));
        setSearchOffset(offset + nextItems.length);
        setHasMoreSearchResults(hasMore);
        return;
      }

      if (!append) {
        _epubsCache = nextItems;
      }
      setEpubs((prev) => (append ? [...prev, ...nextItems] : nextItems));
      setEpubsOffset(offset + nextItems.length);
      setHasMoreEpubs(hasMore);
    } catch (err) {
      console.error("Failed to fetch epubs:", err);
    }
  }, []);

  const refreshEpubs = useCallback(async ({ silent = false } = {}) => {
    if (!silent) {
      setEpubsLoading(true);
    }
    try {
      await fetchEpubPage({ offset: 0, append: false });
    } finally {
      if (!silent) {
        setEpubsLoading(false);
      }
    }
  }, [fetchEpubPage]);

  const loadMoreEpubs = useCallback(async () => {
    if (epubsLoading || epubsLoadingMore || !hasMoreEpubs || isSearchActive) return;
    setEpubsLoadingMore(true);
    try {
      await fetchEpubPage({ offset: epubsOffset, append: true });
    } finally {
      setEpubsLoadingMore(false);
    }
  }, [epubsLoading, epubsLoadingMore, hasMoreEpubs, isSearchActive, fetchEpubPage, epubsOffset]);

  const refreshSearchResults = useCallback(async (query) => {
    const trimmedQuery = query.trim();
    if (!trimmedQuery) {
      setSearchResults([]);
      setSearchOffset(0);
      setHasMoreSearchResults(false);
      setSearchLoading(false);
      setSearchLoadingMore(false);
      return;
    }

    setSearchLoading(true);
    try {
      await fetchEpubPage({ offset: 0, append: false, query: trimmedQuery });
    } finally {
      setSearchLoading(false);
    }
  }, [fetchEpubPage]);

  const loadMoreSearchResults = useCallback(async () => {
    const trimmedQuery = searchQuery.trim();
    if (!trimmedQuery || searchLoading || searchLoadingMore || !hasMoreSearchResults) return;
    setSearchLoadingMore(true);
    try {
      await fetchEpubPage({ offset: searchOffset, append: true, query: trimmedQuery });
    } finally {
      setSearchLoadingMore(false);
    }
  }, [searchQuery, searchLoading, searchLoadingMore, hasMoreSearchResults, fetchEpubPage, searchOffset]);

  useEffect(() => {
    refreshEpubs({ silent: _epubsCache !== null });
  }, [refreshEpubs]);

  useEffect(() => {
    const interval = setInterval(() => {
      if (!isSearchActive) {
        refreshEpubs({ silent: true });
      }
    }, 15000);
    return () => clearInterval(interval);
  }, [isSearchActive, refreshEpubs]);

  useEffect(() => {
    const trimmedQuery = searchQuery.trim();
    const timeoutId = window.setTimeout(() => {
      if (!trimmedQuery) {
        setSearchResults([]);
        setSearchOffset(0);
        setHasMoreSearchResults(false);
        setSearchLoading(false);
        setSearchLoadingMore(false);
        return;
      }
      refreshSearchResults(trimmedQuery);
    }, 180);

    return () => window.clearTimeout(timeoutId);
  }, [searchQuery, refreshSearchResults]);

  useEffect(() => {
    if (!isSearchActive) return undefined;

    const handlePointerDown = (event) => {
      if (!searchSectionRef.current?.contains(event.target)) {
        setIsSearchActive(false);
      }
    };

    document.addEventListener("mousedown", handlePointerDown);
    document.addEventListener("touchstart", handlePointerDown);

    return () => {
      document.removeEventListener("mousedown", handlePointerDown);
      document.removeEventListener("touchstart", handlePointerDown);
    };
  }, [isSearchActive]);

  useEffect(() => {
    const sentinel = loadMoreSentinelRef.current;
    if (!sentinel) return undefined;

    const observer = new IntersectionObserver(
      (entries) => {
        const [entry] = entries;
        if (!entry?.isIntersecting) return;

        if (isSearchActive && searchQuery.trim()) {
          loadMoreSearchResults();
        } else {
          loadMoreEpubs();
        }
      },
      {
        rootMargin: "250px 0px"
      }
    );

    observer.observe(sentinel);
    return () => observer.disconnect();
  }, [
    isSearchActive,
    searchQuery,
    loadMoreEpubs,
    loadMoreSearchResults
  ]);

  return (
    <div className="dashboard-root app-shell-page">
      {showUploadModal && (
        <EpubUploadModal
          onClose={() => setShowUploadModal(false)}
          onUploadSuccess={refreshEpubs}
        />
      )}

      <div className="dashboard-content container-fluid p-3">
        <div
          className={`welcome-signs d-flex justify-content-between align-items-center${
            isSearchActive ? " dashboard-fade-section" : ""
          }`}
        >
          <div className="welcome-left d-flex align-items-center gap-2 justify-content-center">
            <div className="welcome-user-profile me-1">
              {user.profilePicture && !profileImageFailed ? (
                <img
                  src={user.profilePicture}
                  alt="Profile"
                  onError={() => setProfileImageFailed(true)}
                />
              ) : (
                <span className="welcome-user-profile__fallback">
                  {(user?.displayName || "R").charAt(0).toUpperCase()}
                </span>
              )}
            </div>
            <div className="welcome-user">
              <h2 className="welcome-user__heading mb-0">
                Welcome{" "}
                {user.displayName.charAt(0).toUpperCase() +
                  user.displayName.slice(1)}
              </h2>
            </div>
          </div>
          <div className="welcome-right d-flex gap-3 align-items-center">
            <div
              className="add-epub-btn"
              onClick={() => setShowUploadModal(true)}
            >
              <span className="material-symbols-rounded add-epub-icon">
                add_notes
              </span>
            </div>
          </div>
        </div>

        {/* Search bar */}
        <div ref={searchSectionRef}>
          <div
            className={`search-section w-100 pt-4 pb-3${isSearchActive ? " search-section--active" : ""}`}
          >
            <div className="search-wrapper">
              <span className="material-symbols-rounded search-icon">search</span>
              <Button
                as="input"
                type="text"
                placeholder="Search epubs..."
                style={{ paddingLeft: "35px" }}
                value={searchQuery}
                onChange={(event) => setSearchQuery(event.target.value)}
                onFocus={() => setIsSearchActive(true)}
              />
            </div>
          </div>

          {isSearchActive && (
            <div className="dashboard-search-results d-flex flex-column gap-3 pt-2">
              {searchLoading ? (
                <p>Loading...</p>
              ) : searchResults.length === 0 ? (
                <p>No uploads match your search.</p>
              ) : (
                searchResults.map((epub) => (
                  <UploadCard
                    key={`search-${epub.id}`}
                    id={epub.id}
                    title={epub.title}
                    author={epub.author}
                    uploadedBy={epub.uploader_name}
                    uploaderPicture={
                      epub.uploader_picture
                        ? `${process.env.REACT_APP_API_URL.replace("/api", "")}/${epub.uploader_picture}`
                        : null
                    }
                    createdAt={epub.created_at}
                  />
                ))
              )}
              {(searchLoadingMore || hasMoreSearchResults) && searchQuery.trim() && (
                <div ref={loadMoreSentinelRef} className="pt-2">
                  {searchLoadingMore ? <p>Loading more...</p> : null}
                </div>
              )}
            </div>
          )}
        </div>

        <div className={isSearchActive ? "dashboard-fade-section" : ""}>
          <RecentsRow />
        </div>

        <div className={`dash-uploads mt-4${isSearchActive ? " dashboard-fade-section" : ""}`}>
          <div className="alluploads-heading mb-4">
            <h3>All Uploads</h3>
          </div>
          <div className="alluploads-cards d-flex flex-column gap-3 mt-3">
            {epubsLoading ? (
              <p>Loading...</p>
            ) : epubs.length === 0 ? (
              <p>No books uploaded yet.</p>
            ) : (
              epubs.map((epub) => (
                <UploadCard
                  key={epub.id}
                  id={epub.id}
                  title={epub.title}
                  author={epub.author}
                  uploadedBy={epub.uploader_name}
                  uploaderPicture={
                    epub.uploader_picture
                      ? `${process.env.REACT_APP_API_URL.replace("/api", "")}/${epub.uploader_picture}`
                      : null
                  }
                  createdAt={epub.created_at}
                />
              ))
            )}
            {!isSearchActive && (epubsLoadingMore || hasMoreEpubs) && (
              <div ref={loadMoreSentinelRef} className="pt-2">
                {epubsLoadingMore ? <p>Loading more...</p> : null}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
}

export default Dashboard;
